/**
 * Where the money goes.
 *
 * ## The one thing to know before changing anything here
 *
 * **This screen computes nothing.** Every figure on it comes from `gatherLandlordLedger` and
 * `buildStatements` — the code a payout run uses — through `/reports/money`. ADR-0099 is the
 * record of what happened the last time two places worked the same money out: a landlord was shown
 * one figure and paid another, and on the seeded agency the two disagreed by 942,498,950 minor
 * units on a single owner. The report was the wrong one. So the decomposition below is exact by
 * construction rather than by reconciliation: `costs recovered + owed to owners` is `collected for
 * owners`, because that is what `buildStatements` returns per owner and these are its sums.
 *
 * **There is no commission in that decomposition any more, and the absence is the correction.** The
 * rent is the owner's, whole; what the agency collects for itself is the fee the tenant paid on top,
 * on its own line of the same invoice. It is therefore shown *beside* the owners' money rather than
 * taken out of it — a different pot, not a slice of one. ADR-0131.
 *
 * ## One figure the server sends is not drawn, deliberately
 *
 * `/reports/money` also returns `toOwners`, computed as `collected - agencyIncome - recorded -
 * recharged`. On the seeded agency that reads 14,037,764,794 where the owners' own statements sum
 * to 16,929,600,079, and the difference is exactly the agency's income: `collected` is built from
 * the owners' collections alone, so subtracting the agency's fees from it takes them off twice.
 * That is the very error this whole correction was about, surviving in one line of arithmetic, and
 * it is a fault in `moneyOf()` rather than here. Until it is fixed the field is not read — a wrong
 * figure drawn in the right place is worse than one that is absent, because nobody checks it.
 *
 * ## Two figures sit beside the split rather than inside it
 *
 * Money **held as credit** and money that could not be **attributed** to an owner both arrived in
 * the period and belong to nobody yet — credit has no invoice, so no unit, so no owner. They are
 * shown, named and excluded, because a figure that is visible and excluded is a decision, whereas
 * a figure silently folded in is a discrepancy somebody finds a year later.
 *
 * ## And two sections are absent rather than padlocked
 *
 * Expenses need `expense.view` and marketers need `payout.view`; the server sends `null` for
 * either where the caller does not hold it. **A permission is never a gate** — a padlock only a
 * manager can open is noise on a junior's screen — so nothing is drawn at all where a null is.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { CompositionBar, RankedBars, SEGMENT_COLOURS, SegmentKey } from "@/components/charts";
import { Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, PeriodChoice, ReportHeader, ReportState, usePeriod, useCompactMoney } from "./shared";

interface Money {
  from: string;
  to: string;
  /** Everything collected for an owner in the period. The owners' pot, and nothing else. */
  collected: number;
  /** What the agency collected for itself, on its own lines of the same invoices. */
  agencyIncome: number;
  /** Collected for somebody the agency records and holds no balance for — a lawyer's fee. */
  recorded: number;
  recharged: number;
  heldAsCredit: number;
  unattributed: number;
  landlords: Array<{ id: string; name: string; collected: number; expenses: number; net: number }>;
  expensesByCategory: Array<{ category: string; amount: number; recoverable: number }> | null;
  marketers: Array<{ id: string; name: string; earned: number; paid: number; owed: number; sales: number }> | null;
}

export function MoneyReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const { vocab } = useSession();
  const [days, setDays] = usePeriod("days", 365);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-money", days],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - days * 86_400_000);
      return api.get<Money>(
        `/reports/money?from=${from.toISOString().slice(0, 10)}&to=${to.toISOString().slice(0, 10)}`,
      );
    },
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Where the money goes" question="Of what came in, what did we keep and what is owed out?" />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);
  /* What could be recharged, which is deliberately a different figure from what was — see the
     note under the costs chart. Added up from the same rows that chart draws. */
  const recoverable = (data.expensesByCategory ?? []).reduce((sum, row) => sum + row.recoverable, 0);

  /*
    What is owed to the owners: the sum of their own statements.

    Added up from the rows the server sent rather than worked out here — each `net` is
    `buildStatements`' own figure for that owner, the same one the payout run pays and the same one
    the printed statement shows, so this adds nothing and re-derives nothing. It stands in for the
    server's `toOwners`, which is wrong; see the note at the top of this file.
  */
  const owed = data.landlords.reduce((sum, row) => sum + row.net, 0);

  const split = [
    { label: "Recharged costs recovered", value: data.recharged, colour: SEGMENT_COLOURS.recharged },
    { label: `Owed to ${vocab.landlords}`, value: owed, colour: SEGMENT_COLOURS.owners },
  ];

  return (
    <div>
      <ReportHeader
        title="Where the money goes"
        question="Of the rent that came in, what did the agency keep, what did it recover, and what is owed out?"
        subtitle={`${fmt.date(data.from)} to ${fmt.date(data.to)}`}
        actions={
          <PeriodChoice
            label="Over"
            value={days}
            onChange={setDays}
            options={[
              { value: 90, label: "90d" },
              { value: 365, label: "1y" },
              { value: 730, label: "2y" },
            ]}
          />
        }
      />

      <div className="space-y-5">
        <FigureRow>
          <Figure label="Collected for owners" value={compact(data.collected)} sub="Rent applied to an invoice on a unit with an owner" />
          <Figure label="Collected by this agency" value={compact(data.agencyIncome)} tone="good" sub="Its own fees, on their own lines" />
          <Figure label="Costs recovered" value={compact(data.recharged)} sub="Only what the agency actually met" />
          <Figure label={`Owed to ${vocab.landlords}`} value={compact(owed)} sub={`Across ${data.landlords.length} ${data.landlords.length === 1 ? "owner" : "owners"}`} />
        </FigureRow>

        <ChartCard
          title="What the collected rent turned into"
          hint="The two parts add up to the collected figure exactly, because they are the payout run's own arithmetic rather than a second opinion about it. What the agency collected for itself is not in here: it was never part of the rent."
        >
          <CompositionBar label="Rent collected for owners, split two ways" segments={split} format={full} />
          <SegmentKey segments={split} format={full} />
        </ChartCard>

        {/*
          The agency's own income, beside the owners' money rather than inside it.

          Its own card for exactly that reason: putting it in the bar above would say it came out of
          the rent, which is the model this product spent a night undoing.
        */}
        <Card>
          <CardTitle hint="Collected on the same invoices, on lines of their own. None of it is taken from an owner.">
            Collected for somebody other than an owner
          </CardTitle>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="eyebrow">Kept by this agency</dt>
              <dd className="tabular mt-1.5 text-lg font-semibold text-ink-900">{full(data.agencyIncome)}</dd>
              <p className="mt-1 text-xs text-ink-500">Agency fees and the other charges a tenant pays on top.</p>
            </div>
            <div>
              <dt className="eyebrow">Recorded for others</dt>
              <dd className="tabular mt-1.5 text-lg font-semibold text-ink-900">{full(data.recorded)}</dd>
              <p className="mt-1 text-xs text-ink-500">
                Collected for somebody no balance is held for — an agreement fee handed over at the time.
              </p>
            </div>
          </dl>
        </Card>

        {data.heldAsCredit > 0 || data.unattributed > 0 ? (
          <Card>
            <CardTitle hint="Neither figure belongs to an owner yet, so neither is in the split above. Credit has no invoice behind it, so it names no unit and therefore no owner.">
              Arrived, and belongs to nobody yet
            </CardTitle>
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="eyebrow">Held as credit</dt>
                <dd className="tabular mt-1.5 text-lg font-semibold text-ink-900">{full(data.heldAsCredit)}</dd>
                <p className="mt-1 text-xs text-ink-500">Paid in, applied to no invoice.</p>
              </div>
              <div>
                <dt className="eyebrow">Not attributed to an owner</dt>
                <dd className="tabular mt-1.5 text-lg font-semibold text-ink-900">{full(data.unattributed)}</dd>
                <p className="mt-1 text-xs text-ink-500">
                  Settled an invoice, but the unit behind it names nobody — usually the agency's own stock.
                </p>
              </div>
            </dl>
          </Card>
        ) : null}

        <Card padded={false}>
          <div className="p-5 sm:p-6">
            <CardTitle
              action={<span className="text-xs text-ink-400">Same figures the payout run pays</span>}
              hint="Open one to see the statement a landlord is sent, line by line."
            >
              {titleCase(vocab.landlords)}
            </CardTitle>
          </div>
          {data.landlords.length === 0 ? (
            <EmptyState title="Nothing was collected for an owner in this period." />
          ) : (
            <Table
              head={
                <tr>
                  <th>{titleCase(vocab.landlord)}</th>
                  <th className="num">Collected</th>
                  <th className="num">Recharged</th>
                  <th className="num">Owed</th>
                </tr>
              }
            >
              {data.landlords.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/app/reports/statement/${row.id}`} className="link">
                      {row.name}
                    </Link>
                  </td>
                  <td className="num tabular">{full(row.collected)}</td>
                  <td className="num tabular">{row.expenses > 0 ? full(row.expenses) : <span className="text-ink-300">—</span>}</td>
                  <td className="num tabular font-medium">{full(row.net)}</td>
                </tr>
              ))}
              <tr className="font-medium">
                <td>Total</td>
                <td className="num tabular">{full(data.collected)}</td>
                <td className="num tabular">{full(data.recharged)}</td>
                <td className="num tabular">{full(owed)}</td>
              </tr>
            </Table>
          )}
        </Card>

        {data.expensesByCategory ? (
          <ChartCard
            title="What was spent"
            hint="Everything incurred in the period, whoever paid it. Only a cost the agency actually met can be recharged, which is why the recoverable column is smaller than the amount on some rows."
          >
            {data.expensesByCategory.length === 0 ? (
              <EmptyState title="Nothing was spent in this period." />
            ) : (
              <>
                <RankedBars
                  label="Costs by category"
                  rows={data.expensesByCategory.map((row) => ({
                    label: row.category,
                    value: row.amount,
                    sub: row.recoverable > 0 ? `${full(row.recoverable)} marked recoverable` : "not recoverable",
                  }))}
                  format={full}
                  colour={SEGMENT_COLOURS.recharged}
                  footnote={
                    <>
                      <strong>Marked recoverable is not the same figure as recovered.</strong> {full(recoverable)} could
                      be recharged; {full(data.recharged)} actually was, above. The difference is cost that belongs to no
                      one owner in particular — a bill against a block held by several of them — and cannot be put on
                      any single statement.
                    </>
                  }
                />
              </>
            )}
          </ChartCard>
        ) : null}

        {data.marketers && data.marketers.length > 0 ? (
          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="Commission accrues as a buyer's money is allocated, not when the papers are signed — so a plan being paid over four years earns it over four years.">
                Marketers
              </CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>Marketer</th>
                  <th className="num">Sales</th>
                  <th className="num">Earned</th>
                  <th className="num">Paid</th>
                  <th className="num">Owed</th>
                </tr>
              }
            >
              {data.marketers.map((row) => (
                <tr key={row.id}>
                  <td>
                    {/* `/app/marketers/:id` is not a route — the marketer screens live under the module's own
                         prefix. This answered "page not found". */}
                    <Link to={`/app/development/marketers/${row.id}`} className="link">
                      {row.name}
                    </Link>
                  </td>
                  <td className="num tabular text-ink-500">{row.sales}</td>
                  <td className="num tabular">{full(row.earned)}</td>
                  <td className="num tabular">{full(row.paid)}</td>
                  <td className="num tabular font-medium">{full(row.owed)}</td>
                </tr>
              ))}
            </Table>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
